import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
Container,
Box,
Typography,
TextField,
Button,
Tab,
Tabs,
Paper,
Alert,
LinearProgress,
CircularProgress,
Chip,
Grid,
Card,
CardContent,
Divider,
Fade,
Slide,
Avatar,
IconButton,
Tooltip,
useTheme,
useMediaQuery,
Accordion,
AccordionSummary,
AccordionDetails,
Stepper,
Step,
StepLabel,
StepContent
} from '@mui/material';
import {
AddCircleOutline,
RemoveCircleOutline,
CheckCircle,
Error,
ArrowForward,
Article as ArticleIcon,
Edit,
ExpandMore,
School,
AttachMoney,
TrendingUp,
Assessment,
Public,
TrackChanges,
Description
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { APIProjectStore, APIProjectUpdate } from '../../utilities/APIS';
import { getTodaysDate } from '../../utilities/Functions';
import NavigationMenu from '../navigation/NavigationMenu';
import { headers } from '../../utilities/Env';
import { TEXT_CONNECTION_ERROR } from '../../utilities/Text';
import useCustomToast from '../../hooks/useToast';
import LoadingState from '../../components/LoadingState';

const StyledTabs = styled(Tabs)({
    '& .MuiTabs-indicator': {
        backgroundColor: '#1976d2',
        height: 3,
    },
});

const StyledTab = styled(Tab)({
    textTransform: 'none',
    fontWeight: 'bold',
    minWidth: 0,
    padding: '12px 16px',
    fontSize: '1rem',
    '&.Mui-selected': {
        color: '#1976d2',
    },
});

const AnimatedCard = styled(Card)({
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
    },
});

const StyledAccordion = styled(Accordion)({
    '&:before': {
        display: 'none',
    },
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    '&.Mui-expanded': {
        margin: '16px 0',
    },
});

const SubmitProject = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const showToast = useCustomToast();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
        
    const [postStatus, setPostStatus] = useState('ADD'); // 'ADD' or 'UPDATE'
    const [editingProjectId, setEditingProjectId] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [user, setUser] = useState({
        loggedIn: false,
        name: '',
        id: '',
        university: ''
    });

    // Project state
    const [project, setProject] = useState({
        title: '',
        description: '',
        startDate: '',
        endDate: '',
        coreInvestigator: '',
        members: [{ data: '' }],
        objectives: '',
        projectActivities: '',
        proposedBudget: '',
        expectedOutcomes: '',
        evaluationPlan: '',
        sustainabilityPlan: ''
    });

    // UI state
    const [success, setSuccess] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [showSubmit, setShowSubmit] = useState(false);

    // Steps for guided submission
    const steps = [
        { label: 'Basic Information', icon: <Description /> },
        { label: 'Project Details', icon: <TrackChanges /> },
        { label: 'Planning & Budget', icon: <AttachMoney /> },
        { label: 'Impact & Sustainability', icon: <TrendingUp /> }
    ];

    useEffect(() => {
        loadUser();
        checkEditMode();
    }, []);

    // Check if we're in edit mode and load project data
    const checkEditMode = () => {
        if (location.state) {
            const projectData = location.state;
            console.log('Edit mode detected with data:', projectData);
            
            setPostStatus('UPDATE');
            
            // If there's an ID, set it for updating
            if (projectData.projectId) {
                setEditingProjectId(projectData.projectId);
            }

            // Populate form with existing project data
            setProject({
                title: projectData.projectTitle || '',
                description: projectData.projectDescription || '',
                startDate: projectData.projectStart || '',
                endDate: projectData.projectEnd || '',
                coreInvestigator: projectData.coreInvestigator || '',
                members: projectData.projectMembers ? JSON.parse(projectData.projectMembers) : [{ data: '' }],
                objectives: projectData.objectives || '',
                projectActivities: projectData.projectActivities || '',
                proposedBudget: projectData.proposedBudget || '',
                expectedOutcomes: projectData.expectedOutcomes || '',
                evaluationPlan: projectData.evaluationPlan || '',
                sustainabilityPlan: projectData.sustainabilityPlan || ''
            });

            // Enable all tabs since we're editing existing project
            setShowPreview(true);
            setShowSubmit(true);

            showToast("Edit Mode", "You are now editing an existing project", "info");
        }
    };

    const loadUser = () => {
        const userData = sessionStorage.getItem('reseacher');
        if (userData) {
            const { name, id, university, loggedIn } = JSON.parse(userData);
            setUser({ name, id, university, loggedIn });
        }
    };

    const clearProjectsInputs = () => {
        setProject({
            title: '',
            description: '',
            startDate: '',
            endDate: '',
            coreInvestigator: '',
            members: [{ data: '' }],
            objectives: '',
            projectActivities: '',
            proposedBudget: '',
            expectedOutcomes: '',
            evaluationPlan: '',
            sustainabilityPlan: ''
        });
        setShowPreview(false);
        setShowSubmit(false);
        setActiveTab(0);
        setActiveStep(0);
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleNextStep = () => {
        setActiveStep((prevStep) => prevStep + 1);
    };

    const handlePrevStep = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setProject(prev => ({ ...prev, [name]: value }));
        
        // Show preview tab if title is entered
        if (name === 'title' && value.length > 0) {
            setShowPreview(true);
            setShowSubmit(true);
        }
    };

    const handleArrayInputChange = (index, value, field) => {
        const updatedArray = [...project[field]];
        updatedArray[index].data = value;
        setProject(prev => ({ ...prev, [field]: updatedArray }));
    };

    const addInputField = (field) => {
        setProject(prev => ({ ...prev, [field]: [...prev[field], { data: '' }] }));
    };

    const removeInputField = (index, field) => {
        const updatedArray = project[field].filter((_, i) => i !== index);
        setProject(prev => ({ ...prev, [field]: updatedArray }));
    };

    const handleSubmit = async (e) => {
        if (!project.title) {
            showToast("Warning", "Project title is required", "warning");
            return;
        }

        if (!project.coreInvestigator) {
            showToast("Warning", "Core Investigator name is required", "warning");
            return;
        }

        setLoading(true);

        // Create a new FormData object
        const formDataToSend = new FormData();

        formDataToSend.append('uploadDate', getTodaysDate());
        formDataToSend.append('projectTitle', project.title);
        formDataToSend.append('researcher', user.id);
        formDataToSend.append('projectDescription', project.description);
        formDataToSend.append('university', user.university);
        formDataToSend.append('projectStart', project.startDate);
        formDataToSend.append('projectEnd', project.endDate);
        formDataToSend.append('coreInvestigator', project.coreInvestigator);
        formDataToSend.append('projectMembers', JSON.stringify(project.members));
        formDataToSend.append('objectives', project.objectives);
        formDataToSend.append('projectActivities', project.projectActivities);
        formDataToSend.append('proposedBudget', project.proposedBudget);
        formDataToSend.append('expectedOutcomes', project.expectedOutcomes);
        formDataToSend.append('evaluationPlan', project.evaluationPlan);
        formDataToSend.append('sustainabilityPlan', project.sustainabilityPlan);
        
        // Add project ID for update operation
        if (postStatus === "UPDATE" && editingProjectId) {
            formDataToSend.append('updateId', editingProjectId);
        }

        // Debug: log form data
        console.log('Submitting project data:');
        for (let [key, value] of formDataToSend.entries()) {
            console.log(key + ': ' + value);
        }

        try {
            let postRequest;
            if (postStatus === "ADD") {
                postRequest = await axios.post(APIProjectStore, formDataToSend, { headers });
            } else if (postStatus === "UPDATE") {
                postRequest = await axios.post(APIProjectUpdate, formDataToSend, { headers });
            }

            const response = postRequest.data;
            if (response.status === "success") {
                clearProjectsInputs();
                
                const successMessage = postStatus === "ADD" 
                    ? "Project submitted successfully!" 
                    : "Project updated successfully!";
                    
                showToast("Success", successMessage, "success");
                setSuccess(true);
                
                // Auto-navigate after success
                setTimeout(() => navigate("/projects"), 2000);
            } else {
                showToast("Error", "Unknown Server Error, Project Not Created", "error");
            }

        } catch (error) {
            if (error.response) {
                if (error.response.status === 422) {
                    const errors = error.response.data.errors;
                    let errorMessages = [];

                    for (const field in errors) {
                        errorMessages.push(...errors[field]);
                    }

                    showToast(
                        "Validation Error",
                        errorMessages.join("\n"),
                        "error"
                    );
                }
                else {
                    showToast(
                        "Error",
                        error.response.data.message || "An error occurred",
                        "error"
                    );
                }
            }
            else if (error.code === "ERR_NETWORK") {
                showToast("Connection Error", TEXT_CONNECTION_ERROR, "error");
            }
            else {
                showToast("Error", "An unexpected error occurred", "error");
            }
        } finally {
            setLoading(false);
        }
    };

    const renderTeamMembers = () => {
        return (
            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Project Team Members
                </Typography>
                {project.members.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            value={item.data}
                            onChange={(e) => handleArrayInputChange(index, e.target.value, 'members')}
                            placeholder="Enter team member name and role"
                            sx={{ mr: 1 }}
                        />
                        {project.members.length > 1 && (
                            <Tooltip title="Remove">
                                <IconButton 
                                    color="error" 
                                    onClick={() => removeInputField(index, 'members')}
                                >
                                    <RemoveCircleOutline />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                ))}
                <Button
                    variant="outlined"
                    startIcon={<AddCircleOutline />}
                    onClick={() => addInputField('members')}
                    sx={{ mt: 1 }}
                >
                    Add Team Member
                </Button>
            </Box>
        );
    };

    const renderBasicInformation = () => (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <AnimatedCard>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Description color="primary" />
                            Project Basic Information
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        
                        <TextField
                            fullWidth
                            label="Project Title *"
                            name="title"
                            value={project.title}
                            onChange={handleInputChange}
                            margin="normal"
                            required
                            variant="outlined"
                        />
                        
                        <TextField
                            fullWidth
                            label="Project Description"
                            name="description"
                            value={project.description}
                            onChange={handleInputChange}
                            margin="normal"
                            multiline
                            rows={4}
                            placeholder="Provide a brief overview of your project..."
                        />
                        
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Start Date"
                                    name="startDate"
                                    value={project.startDate}
                                    onChange={handleInputChange}
                                    margin="normal"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="End Date"
                                    name="endDate"
                                    value={project.endDate}
                                    onChange={handleInputChange}
                                    margin="normal"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                        </Grid>
                        
                        <TextField
                            fullWidth
                            label="Core Investigator *"
                            name="coreInvestigator"
                            value={project.coreInvestigator}
                            onChange={handleInputChange}
                            margin="normal"
                            required
                            variant="outlined"
                            placeholder="Enter the principal investigator's name"
                        />
                        
                        {renderTeamMembers()}
                    </CardContent>
                </AnimatedCard>
            </Grid>
        </Grid>
    );

    const renderProjectDetails = () => (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <AnimatedCard>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrackChanges color="primary" />
                            Project Objectives & Activities
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        
                        <TextField
                            fullWidth
                            label="Project Objectives"
                            name="objectives"
                            value={project.objectives}
                            onChange={handleInputChange}
                            margin="normal"
                            multiline
                            rows={4}
                            placeholder="List the main objectives of your project..."
                        />
                        
                        <TextField
                            fullWidth
                            label="Project Activities"
                            name="projectActivities"
                            value={project.projectActivities}
                            onChange={handleInputChange}
                            margin="normal"
                            multiline
                            rows={4}
                            placeholder="Describe the key activities to be carried out..."
                        />
                    </CardContent>
                </AnimatedCard>
            </Grid>
        </Grid>
    );

    const renderBudgetAndEvaluation = () => (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <AnimatedCard>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AttachMoney color="primary" />
                            Budget & Evaluation
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        
                        <TextField
                            fullWidth
                            label="Proposed Budget"
                            name="proposedBudget"
                            value={project.proposedBudget}
                            onChange={handleInputChange}
                            margin="normal"
                            multiline
                            rows={4}
                            placeholder="Provide a detailed breakdown of the proposed budget..."
                        />
                        
                        <TextField
                            fullWidth
                            label="Evaluation Plan"
                            name="evaluationPlan"
                            value={project.evaluationPlan}
                            onChange={handleInputChange}
                            margin="normal"
                            multiline
                            rows={4}
                            placeholder="Describe how the project will be evaluated..."
                        />
                    </CardContent>
                </AnimatedCard>
            </Grid>
        </Grid>
    );

    const renderImpactAndSustainability = () => (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <AnimatedCard>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendingUp color="primary" />
                            Impact & Sustainability
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        
                        <TextField
                            fullWidth
                            label="Expected Outcomes"
                            name="expectedOutcomes"
                            value={project.expectedOutcomes}
                            onChange={handleInputChange}
                            margin="normal"
                            multiline
                            rows={4}
                            placeholder="Describe the expected outcomes and deliverables..."
                        />
                        
                        <TextField
                            fullWidth
                            label="Sustainability Plan"
                            name="sustainabilityPlan"
                            value={project.sustainabilityPlan}
                            onChange={handleInputChange}
                            margin="normal"
                            multiline
                            rows={4}
                            placeholder="Explain how the project will be sustained after completion..."
                        />
                    </CardContent>
                </AnimatedCard>
            </Grid>
        </Grid>
    );

    const renderPreview = () => (
        <AnimatedCard>
            <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ArticleIcon color="primary" />
                    Project Preview
                </Typography>
                <Divider sx={{ mb: 3 }} />
                
                <Box sx={{ p: 3, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                        Author
                    </Typography>
                    <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {user.name}
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                        Title
                    </Typography>
                    <Typography variant="h5" gutterBottom sx={{ color: theme.palette.primary.main }}>
                        {project.title}
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                        Description
                    </Typography>
                    <Typography variant="body1" paragraph>
                        {project.description || 'No description provided'}
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Start Date
                            </Typography>
                            <Typography variant="body2">
                                {project.startDate || 'Not specified'}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                End Date
                            </Typography>
                            <Typography variant="body2">
                                {project.endDate || 'Not specified'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Core Investigator
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {project.coreInvestigator || 'Not specified'}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                        Project Team
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {project.members.map((member, index) => (
                            member.data && <Chip key={index} label={member.data} variant="outlined" />
                        ))}
                    </Box>
                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                        Objectives
                    </Typography>
                    <Typography variant="body2" paragraph>
                        {project.objectives || 'Not specified'}
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                        Activities
                    </Typography>
                    <Typography variant="body2" paragraph>
                        {project.projectActivities || 'Not specified'}
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                        Proposed Budget
                    </Typography>
                    <Typography variant="body2" paragraph>
                        {project.proposedBudget || 'Not specified'}
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                        Evaluation Plan
                    </Typography>
                    <Typography variant="body2" paragraph>
                        {project.evaluationPlan || 'Not specified'}
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                        Expected Outcomes
                    </Typography>
                    <Typography variant="body2" paragraph>
                        {project.expectedOutcomes || 'Not specified'}
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                        Sustainability Plan
                    </Typography>
                    <Typography variant="body2" paragraph>
                        {project.sustainabilityPlan || 'Not specified'}
                    </Typography>
                </Box>
            </CardContent>
        </AnimatedCard>
    );

    const renderSubmission = () => (
        <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
                <AnimatedCard>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Submission Progress
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ width: '100%', mr: 1 }}>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={
                                        (project.title ? 10 : 0) +
                                        (project.coreInvestigator ? 10 : 0) +
                                        (project.objectives ? 10 : 0) +
                                        (project.projectActivities ? 10 : 0) +
                                        (project.proposedBudget ? 10 : 0) +
                                        (project.expectedOutcomes ? 10 : 0) +
                                        (project.evaluationPlan ? 10 : 0) +
                                        (project.sustainabilityPlan ? 10 : 0) +
                                        (project.startDate ? 5 : 0) +
                                        (project.endDate ? 5 : 0)
                                    } 
                                />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {Math.min(100,
                                    (project.title ? 10 : 0) +
                                    (project.coreInvestigator ? 10 : 0) +
                                    (project.objectives ? 10 : 0) +
                                    (project.projectActivities ? 10 : 0) +
                                    (project.proposedBudget ? 10 : 0) +
                                    (project.expectedOutcomes ? 10 : 0) +
                                    (project.evaluationPlan ? 10 : 0) +
                                    (project.sustainabilityPlan ? 10 : 0) +
                                    (project.startDate ? 5 : 0) +
                                    (project.endDate ? 5 : 0)
                                )}%
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mt: 2 }}>
                            All required information has been completed. You're ready to {postStatus === 'UPDATE' ? 'update' : 'submit'} your project.
                        </Typography>
                    </CardContent>
                </AnimatedCard>
            </Grid>

            <Grid item xs={12} md={6}>
                <AnimatedCard>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {postStatus === 'UPDATE' ? 'Update Project' : 'Submit Project'}
                        </Typography>
                        {user.loggedIn ? (
                            <>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color={postStatus === 'UPDATE' ? 'warning' : 'primary'}
                                    size="large"
                                    onClick={handleSubmit}
                                    disabled={loading}
                                    startIcon={
                                        loading ? <CircularProgress size={24} /> : 
                                        postStatus === 'UPDATE' ? <Edit /> : <CheckCircle />
                                    }
                                    sx={{ mb: 2 }}
                                >
                                    {loading ? (postStatus === 'UPDATE' ? 'Updating...' : 'Submitting...') : 
                                     (postStatus === 'UPDATE' ? 'Update Project' : 'Submit Project')}
                                </Button>
                                {success && (
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        color="success"
                                        endIcon={<ArrowForward />}
                                        onClick={() => navigate("/projects")}
                                    >
                                        View All Projects
                                    </Button>
                                )}
                            </>
                        ) : (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                You need to be logged in to {postStatus === 'UPDATE' ? 'update' : 'submit'} a project.
                            </Alert>
                        )}
                    </CardContent>
                </AnimatedCard>
            </Grid>
        </Grid>
    );

    const renderGuidedForm = () => (
        <Box sx={{ maxWidth: '100%' }}>
            <Stepper activeStep={activeStep} orientation="vertical" sx={{ mb: 4 }}>
                {steps.map((step, index) => (
                    <Step key={step.label}>
                        <StepLabel StepIconComponent={() => step.icon}>
                            <Typography variant="h6">{step.label}</Typography>
                        </StepLabel>
                        <StepContent>
                            <Box sx={{ mb: 2 }}>
                                {index === 0 && renderBasicInformation()}
                                {index === 1 && renderProjectDetails()}
                                {index === 2 && renderBudgetAndEvaluation()}
                                {index === 3 && renderImpactAndSustainability()}
                                
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                                    <Button
                                        disabled={index === 0}
                                        onClick={handlePrevStep}
                                        variant="outlined"
                                    >
                                        Back
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={index === steps.length - 1 ? () => setActiveTab(2) : handleNextStep}
                                        color="primary"
                                    >
                                        {index === steps.length - 1 ? 'Review & Submit' : 'Continue'}
                                    </Button>
                                </Box>
                            </Box>
                        </StepContent>
                    </Step>
                ))}
            </Stepper>
        </Box>
    );

    if (loading) {
        return (
            <>
                <NavigationMenu />
                <LoadingState/>
            </>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <NavigationMenu />
            <Container Container maxWidth="lg" sx={{ py: 3, flex: 1, marginTop: 8 }}>
                <Slide direction="down" in={true} mountOnEnter unmountOnExit>
                    <Box sx={{
                        mb: 4,
                        p: 3,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        color: 'white',
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }}>
                        <Box>
                            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {postStatus === 'UPDATE' ? 'Edit Project' : 'Submit New Project'}
                            </Typography>
                            <Typography variant="subtitle1">
                                {postStatus === 'UPDATE' ? 'Update your project details' : 'Share your research project with the community'}
                            </Typography>
                            {postStatus === 'UPDATE' && editingProjectId && (
                                <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                                    Editing Project ID: {editingProjectId}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                component={Link}
                                to="/projects"
                                variant="contained"
                                sx={{
                                    bgcolor: 'white',
                                    color: theme.palette.primary.main,
                                    fontWeight: 'bold',
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.9)',
                                        transform: 'translateY(-2px)'
                                    },
                                    transition: 'all 0.3s ease'
                                }}
                                startIcon={<ArticleIcon />}
                            >
                                View Projects
                            </Button>
                        </Box>
                    </Box>
                </Slide>

                <Paper elevation={3} sx={{ mb: 3 }}>
                    <StyledTabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        aria-label="project submission tabs"
                    >
                        <StyledTab label="Project Form" />
                        <StyledTab label="Preview" disabled={!showPreview} />
                        <StyledTab label="Submit" disabled={!showSubmit} />
                    </StyledTabs>
                </Paper>

                {activeTab === 0 && renderGuidedForm()}
                {activeTab === 1 && renderPreview()}
                {activeTab === 2 && renderSubmission()}
            </Container>
        </Box>
    );
};

export default SubmitProject;